import React, { useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { productOrderPaymentSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormHeader } from '../FormHeader'
import { FilePreview } from '@components/file/FilePreview'
import { registerPayment } from '@state/reducers/productOrderReducer'
import { ProductOrderDto, CreateProductOrderPaymentDto } from '@services/dtos'
import { SimpleFileInput } from '@components/inputs/SimpleFileInput'
import { truncate } from 'lodash'
import { Box, Checkbox, FormControlLabel } from '@mui/material'
import { DateInputField } from '@components/inputs/DateInputField'
import moment from 'moment'

interface Props {
  productOrder: ProductOrderDto
  onClose: () => void
}

const ProductOrderPaymentForm: React.FC<Props> = ({ productOrder, onClose }) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateProductOrderPaymentDto>({
    resolver: yupResolver(productOrderPaymentSchema),
    defaultValues: {
      paymentDate: moment().format('YYYY-MM-DD'),
      markAsPaid: false,
      file: null,
    },
  })

  const file = watch('file')
  const markAsPaid = watch('markAsPaid')
  const paymentDate = watch('paymentDate')

  const onSubmitForm = async (data: CreateProductOrderPaymentDto) => {
    setIsSubmitting(true)
    const dto: CreateProductOrderPaymentDto = {
      paymentDate: data.paymentDate,
      markAsPaid: data.markAsPaid,
    }
    dispatch(registerPayment({ id: productOrder.id, dto, file: data.file ?? undefined }))
      .unwrap()
      .then(() => {
        dispatch(enqueueSnackbar({ message: 'Paiement enregistré avec succès', options: { variant: 'success' } }))
        onClose()
      })
      .catch(() => dispatch(enqueueSnackbar({ message: 'Une erreur est survenue', options: { variant: 'error' } })))
      .finally(() => setIsSubmitting(false))
  }

  return (
    <div className="flex w-full h-full p-4">
      <form onSubmit={handleSubmit(onSubmitForm)} className="w-full h-full flex flex-col">
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={'Enregistrer paiement'}
          subTitles={[
            `Commande ${productOrder.internalOrderNumber}`,
            `${truncate(productOrder.supplier.name, { length: 25, omission: '…' })} - ${new Date(productOrder.createdAt).toLocaleDateString('fr-FR')}`,
          ]}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={markAsPaid}
                onChange={(e) => setValue('markAsPaid', e.target.checked)}
              />
            }
            label="Marquer la commande comme réglée"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <DateInputField
              label="Date de paiement"
              value={
                paymentDate
                  ? moment(paymentDate)
                  : null
              }
              onChange={(date) => {
                setValue(
                  'paymentDate',
                  date ? date.format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
                )
              }}
              error={!!errors.paymentDate}
              errorMessage={errors.paymentDate?.message}
              disableFuture
            />
            <SimpleFileInput
              file={file ?? null}
              onSelect={(f) => setValue('file', f)}
              canRemove={true}
            />
          </Box>
        </Box>

        <div className="min-h-[250px] flex items-center justify-center h-[55vh] mt-4">
          <FilePreview file={file} />
        </div>
      </form>
    </div>
  )
}

export default ProductOrderPaymentForm
